import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { news } from "@/mocks/news";
import Tag from "@/components/base/Tag";
import Breadcrumb from "@/components/base/Breadcrumb";
import ShareButtons from "@/components/feature/ShareButtons";
import BrowseHistoryRail from "@/components/feature/BrowseHistoryRail";
import { useSEO } from "@/hooks/useSEO";
import { addHistory } from "@/hooks/useBrowseHistory";

const categoryTone: Record<string, "primary" | "accent" | "secondary"> = {
  新闻: "primary",
  活动: "accent",
  公告: "secondary",
};

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = news.find((n) => n.id === id);

  const related = useMemo(() => {
    if (!item) return [];
    const sameTag = news.filter((n) => n.id !== item.id && n.tags.some((t) => item.tags.includes(t)));
    const sameCat = news.filter((n) => n.id !== item.id && n.category === item.category);
    const merged = [...sameTag, ...sameCat.filter((n) => !sameTag.includes(n))];
    return merged.slice(0, 3);
  }, [item]);

  useSEO({
    title: item ? `${item.title} - 朝桐光影视动态` : "资讯详情 - 朝桐光",
    description: item ? item.summary : "朝桐光影视动态详情页。",
    keywords: item ? `朝桐光,${item.tags.join(",")},影视动态` : "朝桐光,影视动态",
  });

  useEffect(() => {
    if (!item) return;
    addHistory({
      type: "news",
      id: item.id,
      title: item.title,
      subtitle: `${item.category} · ${item.date}`,
      cover: item.cover,
      path: `/news/${item.id}`,
    });
  }, [item]);

  if (!item) {
    return (
      <div className="mx-auto flex w-full max-w-page flex-col items-center px-4 py-32 text-center md:px-6">
        <h1 className="text-2xl font-bold text-foreground-950">未找到该资讯</h1>
        <p className="mt-3 text-sm text-foreground-600">该资讯可能已下线或链接有误。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回影视动态
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto grid w-full max-w-page grid-cols-1 gap-10 px-4 pb-16 pt-28 md:px-6 md:pt-36 lg:grid-cols-3">
      <article className="lg:col-span-2">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视动态", to: "/news" }, { label: "资讯详情" }]} />

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Tag tone={categoryTone[item.category]}>{item.category}</Tag>
          {item.tags.map((t) => (
            <span key={t} className="rounded-full bg-secondary-100 px-3 py-0.5 text-xs text-secondary-800">
              #{t}
            </span>
          ))}
        </div>

        <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
          {item.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-calendar-line"></i>
            {item.date}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-user-line"></i>
            朝桐光官方
          </span>
        </div>

        <div className="mt-6 w-full overflow-hidden rounded-lg" style={{ aspectRatio: "8 / 5" }}>
          <img
            src={item.cover}
            alt={`${item.title} 配图`}
            title={`${item.title} - 朝桐光影视动态`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-8 space-y-5">
          {item.content.map((para, index) => (
            <div key={`p-${index}`}>
              <p className="text-sm leading-8 text-foreground-700 md:text-base">{para}</p>
              {index === 1 && related[0] && (
                <figure className="my-6 w-full overflow-hidden rounded-lg">
                  <img
                    src={related[0].cover}
                    alt={`${related[0].title} 配图`}
                    title={`${related[0].title} - 朝桐光影视动态`}
                    className="h-full w-full object-cover object-top"
                    style={{ aspectRatio: "16 / 9" }}
                  />
                  <figcaption className="mt-2 text-center text-xs text-foreground-400">
                    资料图 · {related[0].title}
                  </figcaption>
                </figure>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-6">
          <ShareButtons title={`${item.title} - 朝桐光`} />
          <Link
            to="/news"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回列表
          </Link>
        </div>
      </article>

      <aside className="flex flex-col gap-6 lg:col-span-1">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
            <span className="flex h-4 w-4 items-center justify-center text-primary-600">
              <i className="ri-links-line"></i>
            </span>
            相关文章推荐
          </h3>
          <ul className="mt-4 space-y-3">
            {related.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/news/${r.id}`}
                  className="group flex gap-3 rounded-md p-1.5 transition-colors hover:bg-background-100"
                >
                  <img
                    src={r.cover}
                    alt={r.title}
                    className="h-14 w-20 shrink-0 rounded-md object-cover object-top"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-700">
                      {r.title}
                    </span>
                    <span className="mt-1 block text-xs text-foreground-500">{r.date}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <BrowseHistoryRail />
      </aside>
    </div>
  );
}